import {compile} from 'path-to-regexp';
import {getPostUrl} from './generateUrlForShare';
import {PostModel} from '@/types/channel/board/board.model';
import {UCC_FILE_TYPE} from '@/constants/channel';
import globalDomains from '@/utils/global-domains';
import {throwAlert} from '@/utils/ui/dialog';
import {
  isAndroid,
  isIOS,
  isMobile,
  mobileModel,
  osVersion,
} from 'react-device-detect';
import {getAppVersion, getSysType, inAppPlayStore} from '../helper';
import {inAppStore} from '../event/util';
import {compareVersions} from 'compare-versions';
import {isValid, format, parse, parseISO} from 'date-fns';
import type {Locale} from 'date-fns';
import {matchPath} from 'react-router-dom';
import tlds from 'tlds';
import {goLogin, isLogin} from '../main';
import {goLinkWithChannelBridge} from '@/components/channel/common/Bridge';
import loadScript from 'load-script';

export const resolvePath = (
  pathTemplate: string,
  params: Record<string, string | number> = {},
): string => {
  const toPath = compile(pathTemplate);

  const stringifiedParams: Record<string, string> = Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, String(value)]),
  );

  return toPath(stringifiedParams);
};

export const isWebview = (() => {
  if (typeof window === 'undefined') {
    return false;
  }

  let uagent = window.navigator?.userAgent.toLocaleLowerCase();
  return uagent.indexOf('afreeca webview') > -1 ||
    uagent.indexOf('afreeca up webview') > -1 ||
    uagent.indexOf('sooplive webview') > -1 ||
    uagent.indexOf('sooplive up webview') > -1
    ? true
    : false;
})();

export const webviewSharePost = (post: PostModel, stationUserId: string) => {
  if (post.ucc && post.ucc.fileType === UCC_FILE_TYPE.CATCHSTORY) {
    webviewShare(
      getPostUrl(post, stationUserId),
      Number(post.date.split('-')[1]) +
        '월' +
        ' ' +
        Number(post.date.split('-')[2]) +
        '일' +
        ' ' +
        '스토리',
    );
  } else {
    webviewShare(getPostUrl(post, stationUserId), post.title);
  }
};

export const webviewShare = (url: string, title: string) => {
  window.location.href = `${process.env.SOOP_APP_SCHEME}share?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
};

/**
 * 객체를 URLSearchParams로 변환해 쿼리스트링을 반환하는 유틸 함수
 * (undefined, null, 빈 값은 제외, 모든 값을 string으로 변환)
 */
export const toQueryString = (
  params: Record<string, string | number | undefined>,
) =>
  new URLSearchParams(
    Object.entries(params)
      .filter(([_, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => [k, String(v)]),
  ).toString();

/**
 * 길이 계산 함수
 * 한글 1글자=2, 영문/숫자/기타 1글자=1로 계산
 */
export const getTextLength = (str: string): number => {
  let length = 0;
  for (const char of str) {
    // 한글 유니코드 범위 체크
    if (char.match(/[가-힣]/)) {
      length += 2;
    } else {
      length += 1;
    }
  }
  return length;
};

/**
 * 숫자에 쉼표(,) 넣는 유틸 함수
 * (undefined, null, 빈 값은 제외, 모든 값을 string으로 변환)
 */
export const numberCommaString = (value: number | string): string => {
  const number = Number(value);
  if (isNaN(number)) {
    return String(value);
  } // 숫자 아닌 경우 그냥 반환
  return number.toLocaleString('ko-KR');
};

/**
 * 쪽지 보내기 버튼 클릭 시
 */
export const handleNoteClicked = async (ids: string[], message?: string) => {
  if (message) {
    await throwAlert({
      message,
    });
    return;
  } else if (!(message || ids.length === 0)) {
    window.open(
      `${globalDomains.NOTE_AFREECATV}/app/index.php?page=write&targetUserId=${ids.join(',')}`,
      '_blank',
      'top=10, left=10, width=500, height=600',
    );
  }
};

export const isChannelPath = (pathname: string): boolean => {
  return pathname.startsWith('/station/');
};

/**
 * 숫자를 한국어 단위로 변환하는 함수
 * @param num - 변환할 숫자
 * @returns 한국어 단위로 변환된 문자열
 */
export const displayNumber = (num = 0): string => {
  const si = [
    {value: 1, symbol: ''},
    {value: 1e3, symbol: '천'},
    {value: 1e4, symbol: '만'},
    {value: 1e8, symbol: '억'},
  ];

  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  let i = 0;
  for (i = si.length - 1; i > 0; i--) {
    if (num >= si[i].value) {
      break;
    }
  }

  // 숫자 소숫점 두자리 부터 버림 정책 적용
  let floorNum = Math.floor((num / si[i].value) * 10) / 10;
  return floorNum.toString().replace(rx, '$1') + si[i].symbol;
};

export const goGiftAdBalloon = (
  streamerId: string,
  stationNo: string,
  callback?: () => void,
): Window | null => {
  if (!stationNo) {
    console.warn('Station number not found');
    return null;
  }

  if (!isLogin()) {
    goLogin();
    return null;
  }

  const APP_SCHEME = process.env.SOOP_APP_SCHEME || 'sooplive://';

  let url = `${globalDomains.ADBALLOON_AFREECATV}/app/api/banner.php?broad_system=station&bj_id=${streamerId}&station_no=${stationNo}`;
  let openPopupId: Window | null = null;

  callback?.();
  if (isWebview) {
    // 광고ID 추가
    const ua = navigator.userAgent;
    const ret = /adid=([0-9A-Za-z\-]+);/g.exec(ua);
    const adid = ret ? ret[1] : 'ADID_DISABLED';
    const webviewUrl = `${url}&adid=${adid}`;

    // 안드로이드 웹뷰 (안드로이드 내 새창의 경우 버그가 존재하여 외부 브라우저로 이동)
    if (isAndroid) {
      /**
       * 안드로이드의 경우 모웹으로 넘어가기 때문에 애드벌룬 선물 처리 후 팝업창 종료가 아니라 유지하기 위해 파라미터 값 전달
       */
      const androidUrl = webviewUrl + `&os=aos_app`;
      // 선물 이벤트 외부 브라우저로 가는 이슈가 있어 방송국 웹뷰로 이동되도록 스킴 변경
      openPopupId = window.open(
        `${APP_SCHEME}browser/station?url=${encodeURIComponent(androidUrl)}`,
        'gift_popup',
        'width=508,height=636',
      );
    } else {
      openPopupId = window.open(
        webviewUrl,
        'gift_popup',
        'width=508,height=636',
      );
    }
  } else {
    // PC, 모바일 웹
    openPopupId = window.open(url, 'gift_popup', 'width=508,height=636');
  }

  return openPopupId;
};

export const popupGiftSticker = (streamerId: string) => {
  window.open(
    `${globalDomains.ST_AFREECATV}/app/pop_gift_sticker.php?szBjId=${streamerId}&sys_type=${getSysType()}&location=station`,
    'gift_popup',
    'width=320,height=240,scrollbars=yes',
  );
};

export const handleStickerBuy = ({
  streamerId,
  callback,
}: {
  streamerId: string;
  callback?: () => void;
}): void => {
  if (!isLogin()) {
    goLogin();
    return;
  }

  callback?.();
  const baseUrl = `${globalDomains.M_AFREECATV}/item/a/stickergift`;
  const params = new URLSearchParams({
    szBjId: streamerId,
    r: Math.random().toString(),
  });

  let url = `${baseUrl}?${params.toString()}`;

  if (isWebview && isAndroid) {
    /**
     * 스티커 구글플레이 인앱 결제 isPlayStore header 추가
     * 안드로이드 특정 웹뷰(ex.afreeca://browser/external) 호출 시
     * userAgent 값이 정상 전달 안되는 이슈 있음
     * 파라미터로 앱 버전 추가해서 전달
     */
    const isPlayStore = inAppPlayStore();
    const version = getAppVersion() || '0.0.0';
    const store = inAppStore();

    // 안드로이드 웹뷰용 추가 파라미터
    const androidParams = new URLSearchParams({
      ref: 'webview',
      isPlayStore: isPlayStore?.toString() || '',
      appVersion: compareVersions(version, '1.0.0') >= 0 ? version : '0',
      szOsVer: osVersion,
      szModel: mobileModel,
    });

    if (store) {
      androidParams.append('store', store.toString());
    }

    url += `&${androidParams.toString()}`;

    window.location.href = `${process.env.SOOP_APP_SCHEME}browser/external?url=${encodeURIComponent(url)}`;
  } else if (isMobile) {
    window.open(isIOS ? goLinkWithChannelBridge(url) : url);
  } else {
    window.open(url, 'gift_popup', 'width=530,height=350,scrollbars=yes');
  }
};

/**
 * yyyyMMdd 문자열을 Date 객체로 변환
 * @param dateStr yyyyMMdd 형식의 날짜 문자열
 * @returns Date 객체
 */
export const parseYYYYMMDD = (dateStr: string): Date => {
  return parse(dateStr, 'yyyyMMdd', new Date());
};

export function formatBytes(a: number, b: number) {
  if (0 === a) {
    return '0 Bytes';
  }
  let c = 1024,
    d = b || 2,
    e = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    f = Math.floor(Math.log(a) / Math.log(c));
  return parseFloat((a / Math.pow(c, f)).toFixed(d)) + ' ' + e[f];
}

/**
 * 날짜 문자열을 기반으로 월 단위로 그룹핑된 문자열을 반환합니다.
 * @param dateStr YYYY-MM-DD
 */
export const getMonthlyGroup = (dateStr: string) => {
  const date = new Date(dateStr);
  return `${date.getMonth() + 1}월 ${date.getFullYear()}`;
};

/**
 * 숲 앱 버전 비교 함수
 * @param version - 비교할 버전
 * @returns 현재 버전이 비교 버전보다 크거나 같은지 여부
 */
export const isSoopAppVersionGreaterOrEqual = (version: string): boolean => {
  const currentVersion = getAppVersion() || '0';
  return compareVersions(currentVersion, version) >= 0;
};

/**
 * 현재 경로가 특정 라우트와 일치하는지 확인하는 헬퍼 함수
 * @param path 비교할 라우트 경로 패턴
 * @param pathname 현재 브라우저 경로
 * @param end 완전 일치 여부 (기본값: true)
 */
export const isMatchingPath = (
  path: string,
  pathname: string,
  end = true,
): boolean => matchPath({path, end}, pathname) !== null;

/**
 * Safari 등 낮은 브라우저 호환을 위한 날짜 파싱 및 포맷터
 * @param date Date 객체 또는 string
 * @param outputFormat 포맷 문자열 (기본값: yyyy-MM-dd)
 * @returns 포맷된 문자열 또는 빈 문자열
 */
export const formatDateSafe = (
  input: string | Date | null | undefined,
  outputFormat = 'yyyy-MM-dd',
  locale?: Locale,
): string => {
  if (!input) {
    return '';
  }

  let date: Date | null = null;

  if (typeof input === 'string') {
    // YYYY-MM-DD → Safari 구버전에서는 실패하므로 슬래시 치환
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      date = new Date(input.replace(/-/g, '/'));
    } else {
      // ISO8601 형식이면 parseISO 사용
      try {
        date = parseISO(input);
      } catch {
        date = new Date(input);
      }
    }
  } else if (typeof input === 'number') {
    // 초/밀리초 대응
    const ms = input < 1e12 ? input * 1000 : input;
    date = new Date(ms);
  } else {
    date = new Date(input);
  }

  if (!date || !isValid(date)) {
    return '';
  }

  return locale
    ? format(date, outputFormat, {locale})
    : format(date, outputFormat);
};

/**
 * 링크 URL의 유효성을 검사하는 함수
 * @param url 검사할 URL 문자열
 * @returns 유효성 검사 결과 객체
 */
export const validateUrl = (url: string) => {
  if (!url || url.trim() === '') {
    return {isValid: false, message: '올바른 URL을 입력해 주세요.'};
  }

  let urlToValidate = url.trim();
  if (
    !urlToValidate.startsWith('http://') &&
    !urlToValidate.startsWith('https://')
  ) {
    urlToValidate = `https://${urlToValidate}`;
  }

  try {
    const urlObj = new URL(urlToValidate);

    // 도메인 형식 검증 (최소 2단계 도메인 구조 필요)
    const domainParts = urlObj.hostname.split('.');
    if (domainParts.length < 2) {
      return {isValid: false, message: '올바른 URL을 입력해 주세요.'};
    }

    // TLD 추출 및 검증
    const tld = domainParts[domainParts.length - 1].toLowerCase();

    // 복합 TLD 검증 (co.kr, com.au 등)
    const secondLevelDomain =
      domainParts.length > 2 ? domainParts[domainParts.length - 2] : '';
    const combinedTld =
      secondLevelDomain && tld ? `${secondLevelDomain}.${tld}` : tld;

    // tlds 라이브러리를 사용하여 유효한 TLD인지 확인
    const isValidTld = tlds.includes(tld) || tlds.includes(combinedTld);

    if (!isValidTld) {
      return {isValid: false, message: '올바른 URL을 입력해 주세요.'};
    }

    return {
      isValid: true,
      message: '',
    };
  } catch (error) {
    return {isValid: false, message: '올바른 URL을 입력해 주세요.'};
  }
};

/**
 * 모바일 게시물 이미지 클릭허용 유무 (새창,확대기능)
 */
export const isImageOpenAllowed = (): boolean => {
  if (!isMobile) {
    return false;
  }
  if (!isWebview) {
    return true;
  }
  return compareVersions(getAppVersion() ?? '', '5.22.0') >= 0;
};

export const checkExistingScript = (url: string) => {
  return Array.from(document.querySelectorAll('script')).find((script) => {
    const urlWithoutQuery = script.src.split('?')[0];
    return urlWithoutQuery === url;
  });
};

export const loadScriptAsync = (url: string, option?: {async?: boolean}) => {
  return new Promise((resolve, reject) => {
    // 이미 스크립트가 로드되어 있는지 확인
    const originalUrl = url.split('?')[0];
    const existingScript = checkExistingScript(originalUrl); // document.querySelector(`script[src="${url}"]`);

    if (existingScript) {
      // 기존 스크립트가 있으면 제거
      if (existingScript.parentElement) {
        existingScript.parentElement.removeChild(existingScript);
      }
    }

    return loadScript(url, (option = {}), (error, script) => {
      if (error) {
        reject(error);
      } else {
        resolve(script);
      }
    });
  });
};
